import { getVersionFromBuildTime } from "@base/BuildEnvironment";
import { ImmHelper } from "@base/util/Immutable";
import { PartialRecord } from "@base/util/TypeUtil";
import { OrderType } from "@biz/Definitions";
import { OrderState, UploadType } from "@biz/definitions/DatabaseDefinitions";
import { ProductionLocation } from "@biz/definitions/SunSailDefinitions";
import { ICustomArticle } from "@biz/definitions/UserProfileDefinitions";
import { ExternalOrdersOld, IExternalOrdersSource } from "@biz/ExternalOrder";
import { ssteToPDE } from "@biz/ISunSailExtended.util";
import { ISunSailExtendedType } from "@biz/ISunSailExtendedType";
import { ILocaContext } from "@biz/language/LocaContext";
import { IExtOrders } from "@biz/models/SceneOrderHelper";
import { IExtendedProductDescriptor, IProductDescriptor } from "@biz/ProductDescriptor";
import { IProjectCheckError } from "@biz/projectCalculation/ProjectDefinitions";
import { IProject } from "@biz/userProjects/new/IProject";
import { ITokenFile, IVariant, VariantDataTypes } from "@biz/userProjects/new/IVariant";
import { Variant } from "@biz/userProjects/new/Variant";

export interface ISceneDownloadInfo {
  timestamp: number;
  user: string;
}

export interface IOrderCheckedStateParams {
  type: "orderChecked";
  productionLocation: ProductionLocation | undefined;
  expectedShippingDate: number | undefined;
  reviewer: string | undefined;
}

export type ChangeOrderStateParams = IOrderCheckedStateParams;

export class SceneItemCommon implements IExternalOrdersSource {
  public _id: string = "";
  public state: OrderState = OrderState.Cancelled;
  public expectedShippingDate: number | undefined;
  public reviewer?: string;
  public projectName: string = "";
  public importId?: string;
  public uploadType: UploadType = UploadType.Offer;
  public user: string = "";
  public removed: boolean = false;
  public uploadTime: number = 0;
  public productDescriptors: IExtendedProductDescriptor[] = [];
  public shoppingCartLength: number | undefined;
  public companyId: string = "";
  public targetPath: string = "";
  public buyerCompanyId: string = "";
  public buyerEmail: string = "";
  public files?: string[];
  public fileMapNew?: PartialRecord<"dealer" | "endCustomer" | "thumb" | "image", ITokenFile>
  public token: string = "";
  public noteCount?: number;
  public specialConsoleCount?: number;
  public customFileCount?: number;
  public productionLocation?: ProductionLocation;
  public extOrders?: IExtOrders;
  /** @deprecated */
  public externalOrders?: ExternalOrdersOld;
  /** @deprecated */
  public fileMap?: Record<string, string>;
  /** @deprecated */
  public sunSailTypes: ISunSailExtendedType[] = [];
}

export class SceneItemSunSailNew extends SceneItemCommon {
  public type: "sceneNew" = "sceneNew" as const;
  public versionBuildTime: number = 0;
  public variantType: VariantDataTypes | undefined;
  public variant: IVariant;
  public project: IProject;
  /**@deprecated*/
  public offers?: PartialRecord<"dealer" | "endCustomer", string>;
  public sentTo: string = "";
  public sentToCC?: string[];
  public errors: IProjectCheckError[] = []; // accepted errors
  public customArticles: Record<string, ICustomArticle> = {};
  public additionalText?: string;
  public orderZipPackageFilled?: number;

  public constructor(variant: IVariant, project: IProject) {
    super();
    this.variant = variant;
    this.project = project;
  }
}

export type SceneItemSummary = Pick<SceneItemSunSailNew, "_id" | "type" | "sunSailTypes" | "productDescriptors" | "uploadType" | "additionalText" | "projectName" | "buyerEmail" | "buyerCompanyId" | "uploadTime" | "state" | "sentTo" | "sentToCC" | "token" | "externalOrders" | "extOrders" | "fileMap" | "fileMapNew" | "offers" | "variantType" | "shoppingCartLength" | "expectedShippingDate">
  & { variant: Pick<SceneItemSunSailNew["variant"], "variantName" | "_id"> }
  & { project: Pick<SceneItemSunSailNew["project"], "_id"> }

export class SceneItemApp extends SceneItemCommon {
  public type: "app" = "app" as const;
  public sceneId: string = "";
  public projectId: string = "";
  public appVersion: string = "";
  public sceneVersion: string = "";
  public variationId: string = "";
  public variationName: string = "";
  public additionalEmails: string = "";
  public orderProjectNumber: string = "";
  public lockedByCompany: string = "";
  public lockedByUser: string = "";
  public staffOverride: string = "";
  public sellerName: string = "";
  public sellerEmail: string = "";
  public isDealer: string = "";
  public buyerName: string = "";
  public downloaded?: ISceneDownloadInfo[];
}

export type SceneItem = SceneItemApp | SceneItemSunSailNew;

export class SceneItemHelper {
  public static getOffer(item: SceneItemSunSailNew, type: "dealer" | "endCustomer"): ITokenFile | undefined {
    return SceneItemHelper.getFile(item, type);
  }

  public static getFile(item: SceneItem | SceneItemSummary, type: "dealer" | "endCustomer" | "thumb" | "image"): ITokenFile | undefined {
    const fileNew = item.fileMapNew?.[type];
    if (fileNew)
      return fileNew;
    const fileOld = item.fileMap?.[type] || (item.type === "sceneNew" ? (item.offers as Record<string, string>)?.[type] : undefined);
    if (fileOld)
      return { id: fileOld, token: "" };
    return undefined;
  }


  static getUpgraded(scene: SceneItem): SceneItem {
    if (scene.type === "sceneNew") {
      return ImmHelper.setProperty(scene, "variant", Variant.upgradeIfOutdated(scene.variant))
    }
    return scene;
  }

  static getName(scene: SceneItem | SceneItemSummary, lc: ILocaContext): string {
    const { projectName, variantName } = SceneItemHelper.getProjectVariantName(scene);
    return (projectName ? projectName : lc.t("Missing_ProjectName")) + (variantName ? " (" + variantName + ")" : "")
  }

  static getProjectVariantName(scene: SceneItem | SceneItemSummary): { projectName: string; variantName: string } {
    const projectName = scene.projectName;
    let variantName = "";
    if (scene.type === "sceneNew")
      variantName = scene?.variant?.variantName;
    if (scene.type === "app")
      variantName = scene.variationId;
    return {
      projectName,
      variantName
    }
  }

  static getProductNumberString(index: number | undefined, total: number | undefined, pre: string = " ", post: string = ""): string {
    if (total === undefined || index === undefined)
      return pre + "";
    return (total > 1 ? `${pre}(${index + 1}/${total})${post}` : "")
  }

  static getProductNumbersString(index: number[], total: number | undefined, pre: string = " ", post: string = ""): string {
    if (total === undefined || index.length === 0)
      return pre + "";
    const mid = index.length === 1 ? "" + (index[0] + 1) : "[" + index.map((i) => "" + (i + 1)) + "]";
    return (total > 1 ? `${pre}(${mid}/${total})${post}` : "")
  }

  static getVersion(scene: SceneItem): string {
    if (scene.type === "app") return scene.appVersion;
    return getVersionFromBuildTime(scene.versionBuildTime);
  }

  static getOwner(item: SceneItem): string | undefined {
    if (item.type === "sceneNew")
      return item.variant.owner;
    return undefined;
  }

  static getSentTo(item: SceneItem): string | undefined {
    if (item.type === "sceneNew")
      return item.sentTo
    return undefined;
  }

  static getSentToCC(item: SceneItem): string[] | undefined {
    if (item.type === "sceneNew")
      return item.sentToCC;
    return undefined;
  }

  static mergeOrderType(a: OrderType | undefined, b: OrderType | undefined): OrderType | undefined {
    if (a === undefined) return b;
    if (b === undefined) return a;
    if (a === b) return a;
    return "HS";
  }

  static getProductDescriptorsExtended(scene: SceneItemCommon | SceneItemSummary | undefined): IExtendedProductDescriptor[] {
    if (!scene)
      return [];
    return scene.productDescriptors ? scene.productDescriptors : ssteToPDE(scene.sunSailTypes)
  }

  static getProductDescriptors(scene: SceneItemCommon | SceneItemSummary | undefined): IProductDescriptor[] {
    return SceneItemHelper.getProductDescriptorsExtended(scene).map((pde) => pde.pd);
  }

  static getProductDescriptorCount(scene: SceneItem | SceneItemSummary) {
    return scene.productDescriptors ? scene.productDescriptors.length : (scene.sunSailTypes?.length || 0);
  }

  static calcMergedOrderType(scene: SceneItem): OrderType | undefined {
    let orderType: OrderType | undefined;
    const productDescriptors = SceneItemHelper.getProductDescriptorsExtended(scene);
    for (const s of productDescriptors) {
      orderType = this.mergeOrderType(orderType, s.orderType);
    }
    return orderType;
  }

  static isValidSail2d(scene: SceneItem): scene is SceneItemSunSailNew {
    return scene.type === "sceneNew" && scene.variant && !Variant.isOutdated(scene.variant);
  }
}